import get from 'lodash/get';
import { Module } from 'vuex';
import { ActionContext, DefineActions, DefineMutations } from 'vuex-type-helper';
import { $http } from '../../app.config';
import { EventBus } from '../../helpers/bus';
import { SessionStorage } from '../../helpers/storage';
import { RootState } from '../index';

export interface Review {
    id: string;
    language: string;
    stars: number;
    status: string;
    text: string;
    title: string;
    updatedAt: string;
    isVerified: boolean;
}

export interface WidgetProps {
    id: string;
    apiKey: string;
    businessUnitId: string;
    locale: string;
    tags: string;
    stars?: string;
}

export interface Widget {
    id: string;
    reviews: Review[];
}

export interface ModuleState {
    isScriptLoaded: boolean;
    widgets: Widget[];
    queued: WidgetProps[];
}

export interface ModuleActions {
    loadScript: void;
    queueLoadReviews: WidgetProps;
    loadReviews: WidgetProps;
}

export interface ModuleMutations {
    setScriptLoaded: boolean;
    updateReviews: { id: string, reviews: Review[] };
    queueLoad: WidgetProps;
    unqueueLoad: WidgetProps;
}

const state: ModuleState = {
    isScriptLoaded: false,
    widgets: [],
    queued: [],
};

const mutations: DefineMutations<ModuleMutations, ModuleState> = {
    updateReviews(s: ModuleState, { id, reviews }): void {
        const widget = { id, reviews };
        const idx = s.widgets.findIndex((w) => w.id === id);
        if (idx > -1) {
            s.widgets.splice(idx, 1, widget);
        } else {
            s.widgets.push(widget);
        }
    },
    setScriptLoaded(s: ModuleState, isLoaded): void {
        s.isScriptLoaded = !!isLoaded;
    },
    queueLoad(s: ModuleState, props): void {
        s.queued.push(props);
    },
    unqueueLoad(s: ModuleState, props): void {
        const idx = s.queued.findIndex((widget) => widget.id === props.id);
        if (idx > -1) {
            s.queued.splice(idx, 1);
        }
    },
};

const actions: DefineActions<ModuleActions, ModuleState, ModuleMutations> = {
    loadScript(s: ActionContext<ModuleState, {}, ModuleActions, ModuleMutations>): void {
        const promises = s.state.queued.map((widget) => {
            return s.dispatch('loadReviews', widget);
        });
        const load = () => {
            if (s.state.isScriptLoaded === false) {
                const script = document.createElement('script');
                script.setAttribute('src', 'https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js');
                document.body.appendChild(script);
                s.commit('setScriptLoaded', true);
            }
        };
        if (promises.length === 0) {
            return load();
        }
        Promise.all(promises).then(() => {
            load();
        }, () => {
            load();
        });
    },
    queueLoadReviews(s: ActionContext<ModuleState, {}, ModuleActions, ModuleMutations>, props): void {
        const { apiKey, businessUnitId, tags } = props;
        if (!apiKey || !businessUnitId || !tags) {
            return;
        }
        s.commit('queueLoad', props);
    },
    loadReviews(s: ActionContext<ModuleState, {}, ModuleActions, ModuleMutations>, props): Promise<Review[]> {
        const { id, apiKey, businessUnitId, tags, stars, locale } = props;

        if (!apiKey || !businessUnitId || !tags) {
            return Promise.resolve([]);
        }

        const url = `https://api.trustpilot.com/v1/business-units/${businessUnitId}/reviews` +
                `?apikey=${apiKey}&tagValue=${tags}&language=${locale}&stars=${stars || ''}&page=1&perPage=1`;

        const processResponseData = (data: any) => {
            const result: Review[] = [];
            const r = get(data, 'reviews', []);
            if (Array.isArray(r) && r.length > 0) {
                r.forEach((d) => {
                    result.push({
                        id: get(d, 'id') || '',
                        status: get(d, 'status') || '',
                        stars: parseInt(get(d, 'stars') || 0, 10),
                        language: get(d, 'language') || '',
                        title: get(d, 'title') || '',
                        text: get(d, 'text') || '',
                        updatedAt: get(d, 'updatedAt') || '',
                        isVerified: Boolean(get(d, 'isVerified') || false),
                    });
                });
            }
            return result;
        };

        const cached = SessionStorage.getItem(url);
        if (cached) {
            const reviews = processResponseData(cached);
            s.commit('updateReviews', { id, reviews });
            s.commit('unqueueLoad', props);
            return Promise.resolve(reviews);
        }

        return new Promise<Review[]>((resolve) => {
            $http.get(url, { errorHandle: false } as any).then(({ data }: { data: any }) => {
                const reviews = processResponseData(data);
                s.commit('updateReviews', { id, reviews });
                s.commit('unqueueLoad', props);
                SessionStorage.setItem(url, data, 24 * 60 * 60 * 1000);
                resolve(reviews);
            }, () => {
                s.commit('updateReviews', { id, reviews: [] });
                s.commit('unqueueLoad', props);
                resolve([]);
            });
        });
    },
};

const module: Module<ModuleState, RootState> = {
    namespaced: true,
    state,
    mutations,
    actions,
};

EventBus.$on('appMounted', (App) => {
    const widgets = Array.from(document.getElementsByClassName('trustpilot-widget'));
    if (widgets.length > 0) {
        setTimeout(() => {
            App.$store.dispatch('trustpilot/loadScript');
        }, 1000);
    }
});

export default module;



// WEBPACK FOOTER //
// ./src/store/modules/trustpilot.ts